/**
 * Utility functions for generating PDF export options for TinyMCE
 * strictly following APA 7th Edition guidelines and institutional overrides.
 */

/**
 * Generates an abbreviated running head from the title (max 50 chars).
 * Rules:
 * - All CAPS
 * - Max 50 chars counting spaces/punctuation
 * - If title <= 50, use full title
 * - If title > 50, truncate to nearest word <= 50
 * - No ellipsis
 * 
 * @param {string} title - The document title
 * @returns {string} - The formatted running head
 */
export const getRunningHead = (title) => {
    if (!title) return 'UNTITLED';

    const upperTitle = title.toUpperCase();

    if (upperTitle.length <= 50) {
        return upperTitle;
    }

    // Truncate to 50 chars
    let truncated = upperTitle.slice(0, 50);

    // If we cut in the middle of a word, find the last space
    // Check if the 51st char was a space (meaning we cut exactly at a word end)
    // or if the current end is not a space
    if (upperTitle[50] !== ' ' && truncated.lastIndexOf(' ') > -1) {
        truncated = truncated.slice(0, truncated.lastIndexOf(' '));
    }

    // Trim any trailing punctuation or spaces
    return truncated.replace(/[.,;:\s]+$/, '');
};

/**
 * Generates the HTML content for a header or footer based on configuration.
 * 
 * @param {object} config - { left: 'title'|'pageNumber'|'none', right: '...' }
 * @param {string} runHead - The running head text
 * @returns {string|null} - HTML string or null if empty
 */
const generateSectionHtml = (config, runHead, type = 'header') => {
    if (!config || (config.left === 'none' && config.right === 'none')) {
        return null;
    }

    // Header gets margin-top, Footer gets margin-bottom for symmetry/alignment
    const verticalMargin = type === 'header' ? 'margin-top: 0.5in; margin-bottom: 0;' : 'margin-top: 0; margin-bottom: 0.5in;';

    const containerStyle = `width: calc(100% - 2in); margin-left: 1in; margin-right: 1in; ${verticalMargin} font-family: "Times New Roman", Times, serif; font-size: 12pt; border: none; border-collapse: collapse;`;
    const leftCellStyle = 'text-align: left; vertical-align: top; width: 50%;';
    const rightCellStyle = 'text-align: right; vertical-align: top; width: 50%;';

    const getContent = (contentType) => {
        if (contentType === 'title') return `<span style="text-transform: uppercase;">${runHead}</span>`;
        if (contentType === 'pageNumber') return '<span class="pageNumber"></span>';
        return '';
    };

    return `
        <table style="${containerStyle}">
            <tr>
                <td style="${leftCellStyle}">${getContent(config.left)}</td>
                <td style="${rightCellStyle}">${getContent(config.right)}</td>
            </tr>
        </table>
    `;
};

/**
 * Gets the default configuration based on document type rules.
 * 
 * @param {string} documentTypeName 
 * @returns {object} - { header: { left, right }, footer: { left, right } }
 */
export const getDefaultExportConfig = (documentTypeName) => {
    const type = documentTypeName?.toLowerCase() || '';

    // 1. Professional Paper / Dissertation / Thesis
    if (
        type.includes('dissertation') ||
        type.includes('thesis') ||
        (type.includes('academic') && type.includes('professional'))
    ) {
        return {
            header: { left: 'title', right: 'pageNumber' },
            footer: { left: 'none', right: 'none' }
        };
    }

    // 2. Student Paper / Honor's Thesis (Default)
    return {
        header: { left: 'none', right: 'pageNumber' },
        footer: { left: 'none', right: 'none' }
    };
};

/**
 * Main function to get the full export options object for TinyMCE.
 * 
 * @param {string} title 
 * @param {string} documentTypeName 
 * @param {object} [customConfig] - Optional override config { header: {...}, footer: {...} }
 * @returns {object}
 */
export const getExportPdfOptions = (title, documentTypeName = 'Academic Paper', customConfig = null) => {
    const runningHead = getRunningHead(title);

    // Use custom config or fallback to defaults based on doc rules
    const config = customConfig || getDefaultExportConfig(documentTypeName);

    const headerHtml = generateSectionHtml(config.header, runningHead, 'header');
    const footerHtml = generateSectionHtml(config.footer, runningHead, 'footer');

    const options = {
        'format': 'A4',
        'margin_top': '1in',
        'margin_right': '1in',
        'margin_bottom': '1in',
        'margin_left': '1in',

        // Header settings
        'header_html': headerHtml || '',
        'header_spacing': '0.5in',
    };

    // Only add footer if it exists to keep margins clean otherwise
    if (footerHtml) {
        options['footer_html'] = footerHtml;
        options['footer_spacing'] = '0.5in';
    }

    return options;
};
